/**
 * Projects API service
 * Handles all project-related API calls
 */

import api from './api'

const projectsService = {
  /**
   * Get all projects where user is a member
   * @param {boolean} includeDeleted - Include soft-deleted projects
   * @returns {Promise} List of projects with pagination
   */
  listProjects: async (includeDeleted = false) => {
    const response = await api.get('/projects/', {
      params: { include_deleted: includeDeleted },
    })
    return response.data
  },

  /**
   * Create a new project
   * @param {Object} data - Project data (name, description)
   * @returns {Promise} Created project data
   */
  createProject: async (data) => {
    const response = await api.post('/projects/', data)
    return response.data
  },

  /**
   * Get project details
   * @param {string} projectId - UUID of the project
   * @returns {Promise} Project details with memberships
   */
  getProject: async (projectId) => {
    const response = await api.get(`/projects/${projectId}/`)
    return response.data
  },

  /**
   * Update project details
   * @param {string} projectId - UUID of the project
   * @param {Object} data - Updated project data
   * @returns {Promise} Updated project data
   */
  updateProject: async (projectId, data) => {
    const response = await api.patch(`/projects/${projectId}/`, data)
    return response.data
  },

  /**
   * Soft delete a project (owner only)
   * @param {string} projectId - UUID of the project
   * @returns {Promise}
   */
  deleteProject: async (projectId) => {
    const response = await api.delete(`/projects/${projectId}/`)
    return response.data
  },

  /**
   * Restore a soft-deleted project (owner only)
   * @param {string} projectId - UUID of the project
   * @returns {Promise} Restored project data
   */
  restoreProject: async (projectId) => {
    const response = await api.post(`/projects/${projectId}/restore/`)
    return response.data
  },

  /**
   * Get project members
   * @param {string} projectId - UUID of the project
   * @returns {Promise} List of project memberships
   */
  listMembers: async (projectId) => {
    const response = await api.get(`/projects/${projectId}/members/`)
    return response.data
  },

  /**
   * Add a member to project
   * @param {string} projectId - UUID of the project
   * @param {number} userId - Integer ID of the user to add
   * @param {string} role - Role: 'editor' or 'viewer'
   * @returns {Promise} Created membership data
   */
  addMember: async (projectId, userId, role) => {
    const response = await api.post(`/projects/${projectId}/add_member/`, {
      user_id: userId,
      role: role,
    })
    return response.data
  },

  /**
   * Update member role
   * @param {string} projectId - UUID of the project
   * @param {string} membershipId - UUID of the membership
   * @param {string} role - New role: 'editor' or 'viewer'
   * @returns {Promise} Updated membership data
   */
  updateMemberRole: async (projectId, membershipId, role) => {
    const response = await api.patch(`/projects/${projectId}/members/${membershipId}/`, {
      role: role,
    })
    return response.data
  },

  /**
   * Remove a member from project
   * @param {string} projectId - UUID of the project
   * @param {string} membershipId - UUID of the membership
   * @returns {Promise}
   */
  removeMember: async (projectId, membershipId) => {
    const response = await api.delete(`/projects/${projectId}/members/${membershipId}/`)
    return response.data
  },
}

export default projectsService
